import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BiMap,
  BiTime,
  BiUser,
  BiChevronRight,
} from 'react-icons/bi';
import { MdSearch } from 'react-icons/md';

import { useShellTabActions } from '@features/products/shell/shellTabActions';
import RunMapView from '@features/mapVisualization/components/RunMapView';
import { classifyRunStopStatus } from '@features/mapVisualization/runTypes';
import type { RunMapStop } from '@features/mapVisualization/runTypes';
import { Mobility_Runs_Bool_Exp } from '@gql/base-types';
import { useGetDealershipLocationQuery } from '../queries/getDealershipLocation.query.generated';
import { useGetServiceRunsMapQuery } from '../queries/getServiceRunsMap.query.generated';
import { useRooftop, useUser } from '@store';

import { useRunLegPolylines } from '../hooks/useRunLegPolylines';
import { MOBILITY_RUN_TILE_STATUSES } from '@hopdrive/feature-order-index';
import RunStatusChip from '@components/Badges/RunStatusChip';

// ── Types ──────────────────────────────────────────────── //

// ── Constants ──────────────────────────────────────────────── //

const MAP_ID = 'runs-map';
const SIDEBAR_WIDTH = 380;

type StatusFilter = 'open' | 'all' | 'active' | 'scheduled' | 'completed' | 'cancelled';

const STATUS_FILTERS: { value: StatusFilter; label: string; statuses: string[] }[] = [
  // Scheduled and Active together: every run still ahead of the dealer. Active alone holds
  // only in_transit (#3220), so opening on it hid a run the dealer just dispatched (#3459).
  { value: 'open', label: 'Scheduled & Active', statuses: [...MOBILITY_RUN_TILE_STATUSES.upcoming, ...MOBILITY_RUN_TILE_STATUSES.active] },
  { value: 'all', label: 'All Runs', statuses: [] },
  { value: 'active', label: 'Active', statuses: MOBILITY_RUN_TILE_STATUSES.active },
  { value: 'scheduled', label: 'Scheduled', statuses: MOBILITY_RUN_TILE_STATUSES.upcoming },
  { value: 'completed', label: 'Completed', statuses: MOBILITY_RUN_TILE_STATUSES.completed },
  { value: 'cancelled', label: 'Canceled', statuses: MOBILITY_RUN_TILE_STATUSES.cancelled },
];

type DateRange = 'today' | 'this_week' | 'this_month' | 'all';

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'all', label: 'All Time' },
];

const getDateBounds = (range: DateRange) => {
  const now = dayjs();
  switch (range) {
    case 'today':
      return { start: now.startOf('day').toISOString(), end: now.endOf('day').toISOString() };
    case 'this_week':
      return { start: now.startOf('week').toISOString(), end: now.endOf('week').toISOString() };
    case 'this_month':
      return { start: now.startOf('month').toISOString(), end: now.endOf('month').toISOString() };
    default:
      return { start: null, end: null };
  }
};

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatTime = (iso?: string | null) => {
  if (!iso) return '—';
  return dayjs(iso).format('M/D h:mm A');
};

// ── Component ──────────────────────────────────────────────── //

/** The Map tab of the Mobile Service shell: the runs map with its sidebar of runs. The layout owns
 *  the tab strip and the header; this tab contributes its refresh as the header's sync. */
const RunsMapTab: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useUser();
  const rooftop = useRooftop();
  const customerId = rooftop?.id || user?.profile?.customer_id || 0;

  // Filters
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('open');
  const [dateRange, setDateRange] = React.useState<DateRange>('this_week');
  const [search, setSearch] = React.useState('');

  // Selection
  const [selectedRunId, setSelectedRunId] = React.useState<number | null>(null);

  // Build filters
  const additionalFilters = React.useMemo<Mobility_Runs_Bool_Exp[]>(() => {
    const filters: Mobility_Runs_Bool_Exp[] = [];
    const sf = STATUS_FILTERS.find(f => f.value === statusFilter);
    if (sf && sf.statuses.length > 0) {
      filters.push({ status: { _in: sf.statuses } });
    }
    const { start, end } = getDateBounds(dateRange);
    if (start && end) {
      filters.push({ departure_time: { _gte: start, _lte: end } });
    }
    return filters;
  }, [statusFilter, dateRange]);

  const { data, loading, refetch } = useGetServiceRunsMapQuery({
    variables: { customerId, additionalFilters },
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
    skip: !customerId,
  });

  const runs = data?.mobility_runs ?? [];

  // The tab's contribution to the header: its refresh.
  const tabActions = React.useMemo(() => ({ sync: refetch }), [refetch]);
  useShellTabActions(tabActions);

  // Apply client-side search filter
  const filteredRuns = React.useMemo(() => {
    if (!search.trim()) return runs;
    const term = search.toLowerCase();
    return runs.filter(r => {
      const driverName = r.driver?.user?.display_name?.toLowerCase() ?? '';
      const stopNames = r.stops
        .map(s => s.service_stop_detail?.[0]?.customer_name?.toLowerCase() ?? '')
        .join(' ');
      return (
        driverName.includes(term) ||
        stopNames.includes(term) ||
        String(r.id).includes(term)
      );
    });
  }, [runs, search]);

  const selectedRun = React.useMemo(() => {
    if (!selectedRunId) return null;
    return filteredRuns.find(r => r.id === selectedRunId) ?? null;
  }, [selectedRunId, filteredRuns]);

  // Build map data from selected run (or show all stops from all runs)
  const mapStops: RunMapStop[] = React.useMemo(() => {
    const targetRuns = selectedRun ? [selectedRun] : filteredRuns;
    return targetRuns.flatMap(run =>
      run.stops
        .filter(s => {
          const lat = s.location?.latitude ?? s.address_lat;
          const lng = s.location?.longitude ?? s.address_lng;
          return lat != null && lng != null;
        })
        .map(s => ({
          id: s.id,
          stopOrder: s.stop_order,
          status: s.status,
          phase: classifyRunStopStatus(s.status),
          address: s.location?.address || s.address || '',
          lat: (s.location?.latitude ?? s.address_lat)!,
          lng: (s.location?.longitude ?? s.address_lng)!,
          location: s.location ? {
            id: s.location.id,
            name: s.location.name,
            address: s.location.address,
            latitude: s.location.latitude,
            longitude: s.location.longitude,
            geofence_radius: s.location.geofence_radius,
            geofence_vertices: null,
            geofence_center_lat: null,
            geofence_center_lng: null,
          } : null,
          customerName: s.service_stop_detail?.[0]?.customer_name || s.location?.name,
        }))
    );
  }, [selectedRun, filteredRuns]);

  // Compute map bounds
  const mapBounds = React.useMemo<google.maps.LatLngBoundsLiteral | undefined>(() => {
    if (mapStops.length === 0) return undefined;
    const lats = mapStops.map(s => s.lat);
    const lngs = mapStops.map(s => s.lng);
    const pad = 0.01;
    return {
      north: Math.max(...lats) + pad,
      south: Math.min(...lats) - pad,
      east: Math.max(...lngs) + pad,
      west: Math.min(...lngs) - pad,
    };
  }, [mapStops]);

  // Leg polylines for selected run
  const legs = React.useMemo(() => {
    if (!selectedRun) return [];
    const stops = selectedRun.stops;
    const result = [];
    for (let i = 0; i < stops.length - 1; i++) {
      result.push({
        fromLocationId: stops[i].location?.id ?? null,
        toLocationId: stops[i + 1].location?.id ?? null,
      });
    }
    return result;
  }, [selectedRun]);

  const { legPolylines } = useRunLegPolylines(customerId, legs);

  // Fly-to bounds when selecting a run
  const flyToBounds = React.useMemo<google.maps.LatLngBoundsLiteral | null>(() => {
    if (!selectedRun) return null;
    const lats: number[] = [];
    const lngs: number[] = [];
    selectedRun.stops.forEach(s => {
      const lat = s.location?.latitude ?? s.address_lat;
      const lng = s.location?.longitude ?? s.address_lng;
      if (lat && lng) { lats.push(lat); lngs.push(lng); }
    });
    if (lats.length === 0) return null;
    return {
      north: Math.max(...lats) + 0.005,
      south: Math.min(...lats) - 0.005,
      east: Math.max(...lngs) + 0.005,
      west: Math.min(...lngs) - 0.005,
    };
  }, [selectedRun]);

  // With nothing to frame (default filters often match zero runs), center on
  // the customer's own store location (customers.location_id — same source as
  // BuildRunDialog) instead of a hardcoded city.
  const { data: dealershipData } = useGetDealershipLocationQuery({
    variables: { customerId },
    skip: !customerId,
  });
  const dealershipLoc = dealershipData?.customers_by_pk?.location;

  const defaultCenter = React.useMemo(() => {
    if (mapBounds) {
      return { lat: (mapBounds.north + mapBounds.south) / 2, lng: (mapBounds.east + mapBounds.west) / 2 };
    }
    if (dealershipLoc?.latitude != null && dealershipLoc?.longitude != null) {
      return { lat: dealershipLoc.latitude, lng: dealershipLoc.longitude };
    }
    return { lat: 37.54, lng: -77.44 }; // last resort when the customer has no store location
  }, [mapBounds, dealershipLoc]);

  // Height measurement
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState('100%');
  React.useEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        const top = contentRef.current.getBoundingClientRect().top;
        setContentHeight(`${window.innerHeight - top}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data]);

  return (
    <>
      {loading && !data ? <LinearProgress /> : null}

      <Box ref={contentRef} sx={{ display: 'flex', overflow: 'hidden', height: contentHeight }}>
        {/* ── Sidebar ── */}
        <Box sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          overflowY: 'auto',
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Filters */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              select
              size='small'
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value as StatusFilter); setSelectedRunId(null); }}
              sx={{ flex: 1, minWidth: 100 }}
            >
              {STATUS_FILTERS.map(f => (
                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size='small'
              value={dateRange}
              onChange={e => { setDateRange(e.target.value as DateRange); setSelectedRunId(null); }}
              sx={{ flex: 1, minWidth: 100 }}
            >
              {DATE_RANGES.map(r => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              size='small'
              fullWidth
              placeholder='Search runs...'
              value={search}
              onChange={e => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: <MdSearch size={16} color={theme.palette.text.disabled} style={{ marginRight: 6 }} />,
                },
              }}
            />
          </Box>

          <Divider />

          {/* Run count */}
          <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              {filteredRuns.length} run{filteredRuns.length !== 1 ? 's' : ''}
            </Typography>
            {selectedRunId ? (
              <Chip
                label='Clear selection'
                size='small'
                onDelete={() => setSelectedRunId(null)}
                sx={{ fontSize: 10, height: 20 }}
              />
            ) : null}
          </Box>

          <Divider />

          {/* Runs list */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {filteredRuns.map(run => {
              const isSelected = selectedRunId === run.id;
              const stopCount = run.stops_aggregate?.aggregate?.count ?? run.stops.length;
              const driverName = run.driver?.user?.display_name;

              return (
                <Box
                  key={run.id}
                  onClick={() => setSelectedRunId(isSelected ? null : run.id)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    transition: 'background-color 0.1s',
                    '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant='body2' sx={{ fontWeight: 700, flex: 1 }}>
                      Run #{run.id}
                    </Typography>
                    <RunStatusChip status={run.status} sx={{ fontSize: 10, fontWeight: 600, height: 20 }} />
                    <BiChevronRight
                      size={16}
                      color={theme.palette.text.disabled}
                      style={{ cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); navigate(`/mobile-service/runs/${run.id}`); }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BiUser size={12} color={theme.palette.text.secondary} />
                      <Typography variant='caption' color='text.secondary'>
                        {driverName || 'Unassigned'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BiMap size={12} color={theme.palette.text.secondary} />
                      <Typography variant='caption' color='text.secondary'>
                        {stopCount} stop{stopCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <BiTime size={12} color={theme.palette.text.secondary} />
                      <Typography variant='caption' color='text.secondary'>
                        {formatTime(run.departure_time)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Show stop names when selected */}
                  {isSelected ? (
                    <Box sx={{ mt: 1, ml: 0.5 }}>
                      {run.stops.map((stop, i) => {
                        const detail = stop.service_stop_detail?.[0];
                        const isFirst = i === 0;
                        const isLast = i === run.stops.length - 1;
                        return (
                          <Box key={stop.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                            <Avatar sx={{
                              width: 18,
                              height: 18,
                              fontSize: 9,
                              fontWeight: 700,
                              bgcolor: (isFirst || isLast) ? '#3b82f6' : 'secondaryAccent.main',
                            }}>
                              {stop.stop_order}
                            </Avatar>
                            <Typography variant='caption' color='text.secondary' noWrap>
                              {(isFirst || isLast)
                                ? (isFirst ? 'Depart Dealership' : 'Return to Dealership')
                                : (detail?.customer_name || stop.location?.name || stop.address || 'Stop')
                              }
                            </Typography>
                            <Chip
                              label={formatStatus(stop.status)}
                              size='small'
                              sx={{ fontSize: 8, height: 16, ml: 'auto' }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}
                </Box>
              );
            })}

            {!loading && filteredRuns.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant='body2' color='text.secondary'>
                  No runs match your filters.
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* ── Map ── */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <RunMapView
            mapId={MAP_ID}
            stops={mapStops}
            legPolylines={selectedRun ? legPolylines : undefined}
            defaultCenter={defaultCenter}
            defaultZoom={11}
            defaultBounds={mapBounds}
            flyToBounds={flyToBounds}
            showGeofences={!!selectedRun}
          />
        </Box>
      </Box>
    </>
  );
};

export default RunsMapTab;
